/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { $isCodeHighlightNode } from "@lexical/code";
import { $isLinkNode } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import { useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";
import { createPortal } from "react-dom";
import {
  Flex,
  Icon,
  IconButton,
  useBreakpointValue,
  useColorMode,
} from "@chakra-ui/react";
import { ReactComponent as FormatBoldIcon } from "assets/icons/format-bold.svg";
import { ReactComponent as FormatItalicIcon } from "assets/icons/format-italic.svg";
import { ReactComponent as FormatSubscriptIcon } from "assets/icons/format-subscript.svg";
import { ReactComponent as FormatSuperscriptIcon } from "assets/icons/format-superscript.svg";
import { ReactComponent as FormatCodeIcon } from "assets/icons/code-tags.svg";

import { getDOMRangeRect } from "../utils/get-dom-range-rect";
import { getSelectedNode } from "../utils/get-selected-node";
import { setFloatingElemPosition } from "../utils/set-floating-element-position";

function TextFormatFloatingToolbar({
  editor,
  anchorElem,
  isBold,
  isItalic,
  isUnderline,
  isCode,
  isStrikethrough,
  isSubscript,
  isSuperscript,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isBold: boolean;
  isCode: boolean;
  isItalic: boolean;
  isStrikethrough: boolean;
  isSubscript: boolean;
  isSuperscript: boolean;
  isUnderline: boolean;
}): JSX.Element {
  const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null);

  function mouseMoveListener(e: MouseEvent) {
    if (
      popupCharStylesEditorRef?.current &&
      (e.buttons === 1 || e.buttons === 3)
    ) {
      popupCharStylesEditorRef.current.style.pointerEvents = "none";
    }
  }
  function mouseUpListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current) {
      popupCharStylesEditorRef.current.style.pointerEvents = "auto";
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener("mousemove", mouseMoveListener);
      document.addEventListener("mouseup", mouseUpListener);

      return () => {
        document.removeEventListener("mousemove", mouseMoveListener);
        document.removeEventListener("mouseup", mouseUpListener);
      };
    }
  }, [popupCharStylesEditorRef]);

  const updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection();

    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    const nativeSelection = window.getSelection();

    if (popupCharStylesEditorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement);

      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem);
    }
  }, [editor, anchorElem]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        updateTextFormatFloatingToolbar();
      });
    };

    window.addEventListener("resize", update);
    if (scrollerElem) {
      scrollerElem.addEventListener("scroll", update);
    }

    return () => {
      window.removeEventListener("resize", update);
      if (scrollerElem) {
        scrollerElem.removeEventListener("scroll", update);
      }
    };
  }, [editor, updateTextFormatFloatingToolbar, anchorElem]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateTextFormatFloatingToolbar();
    });
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateTextFormatFloatingToolbar();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateTextFormatFloatingToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, updateTextFormatFloatingToolbar]);

  const { colorMode } = useColorMode();
  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );

  return !editor.isEditable() ? (
    <></>
  ) : (
    <Flex
      id="editor-ultra-toolbar"
      ref={popupCharStylesEditorRef}
      gridGap={2}
      p={1}
      align="center"
      position="absolute"
      top={0}
      left={0}
      z-index={10}
      opacity={0}
      tabIndex={0}
      borderRadius="md"
      borderWidth="1px"
      borderColor={colorMode === "dark" ? "gray.700" : "gray.300"}
      transition="opacity 0.5s"
      willChange="transform"
      backgroundColor={colorMode === "dark" ? "gray.800" : "white"}
    >
      <IconButton
        variant="ghost"
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
        }}
        size={isBaseSize ? "sm" : "xs"}
        p={1}
        colorScheme="blackAlpha"
        borderRadius="sm"
        disabled={
          isItalic ||
          isUnderline ||
          isStrikethrough ||
          isSuperscript ||
          isSubscript ||
          isCode
        }
        backgroundColor={isBold ? "gray.300" : "unset"}
        aria-label="Format text as bold"
        icon={<Icon as={FormatBoldIcon} />}
      />
      <IconButton
        size={isBaseSize ? "sm" : "xs"}
        variant="ghost"
        p={1}
        colorScheme="blackAlpha"
        borderRadius="sm"
        backgroundColor={isItalic ? "gray.300" : "unset"}
        disabled={
          isBold ||
          isUnderline ||
          isStrikethrough ||
          isSuperscript ||
          isSubscript ||
          isCode
        }
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
        }}
        aria-label="Format text as italics"
        icon={<Icon as={FormatItalicIcon} />}
      />
      <IconButton
        size={isBaseSize ? "sm" : "xs"}
        variant="ghost"
        colorScheme="blackAlpha"
        p={1}
        borderRadius="sm"
        disabled={
          isBold ||
          isItalic ||
          isStrikethrough ||
          isUnderline ||
          isSuperscript ||
          isCode
        }
        backgroundColor={isSubscript ? "gray.300" : "unset"}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "subscript");
        }}
        title="Subscript"
        aria-label="Format Subscript"
        icon={<Icon as={FormatSubscriptIcon} />}
      />
      <IconButton
        size={isBaseSize ? "sm" : "xs"}
        variant="ghost"
        p={1}
        colorScheme="blackAlpha"
        borderRadius="sm"
        disabled={
          isBold ||
          isItalic ||
          isStrikethrough ||
          isUnderline ||
          isSubscript ||
          isCode
        }
        backgroundColor={isSuperscript ? "gray.300" : "unset"}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "superscript");
        }}
        title="Superscript"
        aria-label="Format Superscript"
        icon={<Icon as={FormatSuperscriptIcon} />}
      />
      <IconButton
        size={isBaseSize ? "sm" : "xs"}
        variant="ghost"
        p={1}
        colorScheme="blackAlpha"
        borderRadius="sm"
        disabled={
          isBold ||
          isItalic ||
          isStrikethrough ||
          isUnderline ||
          isSubscript ||
          isSuperscript
        }
        backgroundColor={isCode ? "gray.300" : "unset"}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
        }}
        aria-label="Insert code block"
        icon={<Icon as={FormatCodeIcon} />}
      />
    </Flex>
  );
}

function useFloatingTextFormatToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement
): JSX.Element | null {
  const [isText, setIsText] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isCode, setIsCode] = useState(false);

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      // Should not to pop up the floating toolbar when using IME input
      if (editor.isComposing()) {
        return;
      }
      const selection = $getSelection();
      const nativeSelection = window.getSelection();
      const rootElement = editor.getRootElement();

      if (
        nativeSelection !== null &&
        (!$isRangeSelection(selection) ||
          rootElement === null ||
          !rootElement.contains(nativeSelection.anchorNode))
      ) {
        setIsText(false);
        return;
      }

      if (!$isRangeSelection(selection)) {
        return;
      }

      const node = getSelectedNode(selection);

      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsSubscript(selection.hasFormat("subscript"));
      setIsSuperscript(selection.hasFormat("superscript"));
      setIsCode(selection.hasFormat("code"));

      // Update links
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      if (
        !$isCodeHighlightNode(selection.anchor.getNode()) &&
        selection.getTextContent() !== ""
      ) {
        setIsText($isTextNode(node));
      } else {
        setIsText(false);
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, "");
      if (!selection.isCollapsed() && rawTextContent === "") {
        setIsText(false);
        // eslint-disable-next-line
        return;
      }
    });
  }, [editor]);

  useEffect(() => {
    document.addEventListener("selectionchange", updatePopup);
    return () => {
      document.removeEventListener("selectionchange", updatePopup);
    };
  }, [updatePopup]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup();
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setIsText(false);
        }
      })
    );
  }, [editor, updatePopup]);

  if (!isText || isLink) {
    return null;
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isBold={isBold}
      isItalic={isItalic}
      isStrikethrough={isStrikethrough}
      isSubscript={isSubscript}
      isSuperscript={isSuperscript}
      isUnderline={isUnderline}
      isCode={isCode}
    />,
    anchorElem
  );
}

export default function FloatingTextFormatToolbarPlugin({
  anchorElem = document.body,
}: {
  anchorElem?: HTMLElement;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingTextFormatToolbar(editor, anchorElem);
}
